import {
  DoseGuardFields,
  needsConfirmation,
  toDoseGuardFormValues,
  toDoseGuardSettings,
  type DoseGuardFormValues,
} from './DoseGuardFields';
import { createTask } from '../../services/taskService';
import type { DoseGuardResult } from '../../services/dosingScheduleService';
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { z } from 'zod';
import { useTranslation } from '../../i18n/useTranslation';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { Stack } from '../atoms/Stack/Stack';
import { Inline } from '../atoms/Inline/Inline';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { DEFAULT_SINGLE_DOSE_TIME, doseTimesFor } from './doseTimes';
import {
  createDosingSchedule,
  updateDosingSchedule,
  type DosingSchedule,
  type DosingScheduleFrequency,
  type CreateDosingScheduleInput,
  type UpdateDosingScheduleInput,
} from '../../services/dosingScheduleService';
import { toDosingSaveError, type DosingSaveError } from './dosingSaveError';
import './DosingScheduleForm.css';
import { FieldError } from '../atoms/FieldError/FieldError';

const FREQUENCY_VALUES = [
  'TIMES_PER_DAY',
  'EVERY_X_DAYS',
  'TIMES_PER_WEEK',
  'SPECIFIC_DAYS',
  'WEEKLY',
  'EVERY_X_WEEKS',
] as const;

const WEEKDAY_FREQUENCIES: ReadonlySet<DosingScheduleFrequency> = new Set([
  'SPECIFIC_DAYS',
  'WEEKLY',
  'EVERY_X_WEEKS',
]);

const INTERVAL_DAYS_FREQUENCIES: ReadonlySet<DosingScheduleFrequency> = new Set(['EVERY_X_DAYS', 'EVERY_X_WEEKS']);

const TIMES_PER_FREQUENCIES: ReadonlySet<DosingScheduleFrequency> = new Set(['TIMES_PER_DAY', 'TIMES_PER_WEEK']);

// Numeric weekday convention: 0=Sunday .. 6=Saturday (matches Date.getDay() and
// the DosingSchedule.weekdays: number[] | null contract in dosingScheduleService.ts).
const WEEKDAY_NUMBERS = [0, 1, 2, 3, 4, 5, 6] as const;

type WeekdayNumber = (typeof WEEKDAY_NUMBERS)[number];

const WEEKDAY_LABEL_KEYS = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
} as const satisfies Record<WeekdayNumber, string>;

export interface DosingScheduleFormValues {
  productName: string;
  doseAmount: string;
  doseUnit: string;
  frequency: DosingScheduleFrequency;
  weekdays: WeekdayNumber[];
  intervalDays: string;
  timesPerDay: string;
  timeOfDay: string;
  /** "Dosierung über Dosierpumpe": the plan's doses are logged automatically. */
  dosingPump: boolean;
  /** Edited clock times of a TIMES_PER_DAY pump plan (one per dose). */
  doseTimes: string[];
  /** 'YYYY-MM-DD'; empty = no restriction. */
  startDate: string;
  /** 'YYYY-MM-DD'; empty = open-ended. */
  endDate: string;
  notes: string;
}

/** Today in the browser's local time as 'YYYY-MM-DD' (the default start date of a new plan). */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Accepts the German decimal comma ("1,1") as well as "1.1". */
export function parseDecimal(value: string): number {
  const normalised = value.trim().replace(',', '.');
  return normalised.length === 0 ? Number.NaN : Number(normalised);
}

function isWholeNumberIn(value: string, min: number, max: number): boolean {
  const n = parseDecimal(value);
  return Number.isInteger(n) && n >= min && n <= max;
}

const dosingScheduleFormSchema = z
  .object({
    productName: z.string().trim().min(1).max(200),
    doseAmount: z
      .string()
      .trim()
      .min(1)
      .refine((val) => {
        const n = parseDecimal(val);
        return Number.isFinite(n) && n > 0 && n <= 99999;
      }, {
        message: 'invalid-dose-amount',
      }),
    doseUnit: z.string().trim().min(1).max(20),
    frequency: z.enum(FREQUENCY_VALUES),
    weekdays: z.array(z.number().int().min(0).max(6)),
    intervalDays: z.string().trim(),
    timesPerDay: z.string().trim(),
    timeOfDay: z.string().trim(),
    dosingPump: z.boolean(),
    doseTimes: z.array(z.string()),
    startDate: z.string().trim(),
    endDate: z.string().trim(),
    notes: z.string().max(1000),
  })
  .superRefine((data, ctx) => {
    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'end-before-start', path: ['endDate'] });
    }
    if (WEEKDAY_FREQUENCIES.has(data.frequency) && data.weekdays.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'weekdays-required',
        path: ['weekdays'],
      });
    }
    if (INTERVAL_DAYS_FREQUENCIES.has(data.frequency)) {
      if (!isWholeNumberIn(data.intervalDays, 1, 365)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'invalid-interval-days',
          path: ['intervalDays'],
        });
      }
    }
    if (TIMES_PER_FREQUENCIES.has(data.frequency)) {
      if (!isWholeNumberIn(data.timesPerDay, 1, 24)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'invalid-times-per-day',
          path: ['timesPerDay'],
        });
      }
    }
    if (!INTERVAL_DAYS_FREQUENCIES.has(data.frequency) && !TIMES_PER_FREQUENCIES.has(data.frequency)) {
      if (data.timeOfDay.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'invalid-time-of-day',
          path: ['timeOfDay'],
        });
      }
    }
  });

function isWeekdayFrequency(frequency: DosingScheduleFrequency): boolean {
  return WEEKDAY_FREQUENCIES.has(frequency);
}

function isIntervalDaysFrequency(frequency: DosingScheduleFrequency): boolean {
  return INTERVAL_DAYS_FREQUENCIES.has(frequency);
}

function isTimesPerFrequency(frequency: DosingScheduleFrequency): boolean {
  return TIMES_PER_FREQUENCIES.has(frequency);
}

function toFormValues(schedule?: DosingSchedule): DosingScheduleFormValues {
  if (!schedule) {
    return {
      productName: '',
      doseAmount: '',
      doseUnit: '',
      frequency: 'TIMES_PER_DAY',
      weekdays: [],
      intervalDays: '',
      timesPerDay: '',
      timeOfDay: '',
      dosingPump: false,
      doseTimes: [],
      startDate: todayLocal(),
      endDate: '',
      notes: '',
    };
  }
  return {
    productName: schedule.product_name,
    doseAmount: String(schedule.dose_amount),
    doseUnit: schedule.dose_unit ?? '',
    frequency: schedule.frequency,
    weekdays: (schedule.weekdays ?? []) as WeekdayNumber[],
    intervalDays: schedule.interval_days !== null && schedule.interval_days !== undefined ? String(schedule.interval_days) : '',
    timesPerDay: schedule.times_per_day !== null && schedule.times_per_day !== undefined ? String(schedule.times_per_day) : '',
    timeOfDay: schedule.time_of_day ?? '',
    dosingPump: schedule.dosing_pump ?? false,
    doseTimes: schedule.dose_times ?? [],
    startDate: schedule.start_date ?? '',
    endDate: schedule.end_date ?? '',
    notes: schedule.notes ?? '',
  };
}

export interface DosingScheduleFormProps {
  aquariumId: string;
  schedule?: DosingSchedule;
  /** A new dose suggested by a voice note (UC-4): prefilled in place of the plan's dose; the dose guard checks it as usual. */
  suggestedDoseAmount?: number;
  onSuccess: (schedule: DosingSchedule) => void;
  onCancel: () => void;
}

function initialValues(schedule: DosingSchedule | undefined, suggestedDoseAmount: number | undefined): DosingScheduleFormValues {
  const values = toFormValues(schedule);
  return schedule && suggestedDoseAmount !== undefined ? { ...values, doseAmount: String(suggestedDoseAmount) } : values;
}

export function DosingScheduleForm({ aquariumId, schedule, suggestedDoseAmount, onSuccess, onCancel }: DosingScheduleFormProps) {
  const { t, i18n } = useTranslation();
  const isEdit = Boolean(schedule);

  const [values, setValues] = useState<DosingScheduleFormValues>(() => initialValues(schedule, suggestedDoseAmount));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof DosingScheduleFormValues, string>>>({});
  const [saveError, setSaveError] = useState<DosingSaveError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [doseGuard, setDoseGuard] = useState<DoseGuardFormValues>(() => toDoseGuardFormValues(schedule?.dose_guard));
  const [guardResult, setGuardResult] = useState<DoseGuardResult | null>(null);
  const [guardConfirmed, setGuardConfirmed] = useState(false);
  const [guardConfirmError, setGuardConfirmError] = useState(false);

  useEffect(() => {
    setValues(initialValues(schedule, suggestedDoseAmount));
    setDoseGuard(toDoseGuardFormValues(schedule?.dose_guard));
    setFieldErrors({});
    setSaveError(null);
  }, [schedule, suggestedDoseAmount]);

  // A changed check needs a fresh confirmation.
  useEffect(() => {
    setGuardConfirmed(false);
    setGuardConfirmError(false);
  }, [guardResult?.status, guardResult?.perDay]);

  const showWeekdays = isWeekdayFrequency(values.frequency);
  const showIntervalDays = isIntervalDaysFrequency(values.frequency);
  const showTimesPerDay = isTimesPerFrequency(values.frequency);
  const showTimeOfDay = !showIntervalDays && !showTimesPerDay;
  // Every plan has concrete dose times — pump plans log them automatically,
  // manual plans are due at them in Aufgaben: N inputs for "N× per day", one
  // (prefilled 09:00) for the frequencies without a time-of-day field.
  const timesPerDayCount = isWholeNumberIn(values.timesPerDay, 1, 24) ? parseDecimal(values.timesPerDay) : null;
  const showPumpDoseTimes = values.frequency === 'TIMES_PER_DAY' && timesPerDayCount !== null;
  const shownDoseTimes = showPumpDoseTimes && timesPerDayCount !== null ? doseTimesFor(timesPerDayCount, values.doseTimes) : [];
  const showPumpTime = !showTimeOfDay && values.frequency !== 'TIMES_PER_DAY';
  const pumpTimeValue = values.timeOfDay.length > 0 ? values.timeOfDay : DEFAULT_SINGLE_DOSE_TIME;

  function handleDoseTimeChange(index: number, value: string): void {
    const next = [...shownDoseTimes];
    next[index] = value;
    setValues((prev) => ({ ...prev, doseTimes: next }));
  }

  function handleChange<K extends keyof DosingScheduleFormValues>(key: K, value: DosingScheduleFormValues[K]): void {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function toggleWeekday(day: WeekdayNumber): void {
    setValues((prev) => {
      const has = prev.weekdays.includes(day);
      return {
        ...prev,
        weekdays: has ? prev.weekdays.filter((d) => d !== day) : [...prev.weekdays, day],
      };
    });
  }

  /** Reef Guard: re-test the dosed element ~24 h after a dosing change (a task due tomorrow). Never blocks saving. */
  async function scheduleRetest(guard: ReturnType<typeof toDoseGuardSettings>): Promise<void> {
    if (!guard || guard.product === 'kalkwasser') return;
    const tomorrow = new Date(Date.now() + 24 * 3_600_000);
    const due = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    try {
      await createTask(aquariumId, {
        title: t('dosing.guard.retestTitle', { parameter: t(`dosing.guard.elements.${guard.element}`) }),
        description: t('dosing.guard.retestDescription'),
        priority: 'medium',
        category: 'water_testing',
        dueDate: due,
      });
    } catch {
      // The plan is saved; a missing reminder must not turn that into an error.
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveError(null);

    const parsed = dosingScheduleFormSchema.safeParse(values);
    if (!parsed.success) {
      const errors: Partial<Record<keyof DosingScheduleFormValues, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof DosingScheduleFormValues;
        errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }
    if (showPumpDoseTimes) {
      const valid = shownDoseTimes.every((time) => HH_MM.test(time)) && new Set(shownDoseTimes).size === shownDoseTimes.length;
      if (!valid) {
        setFieldErrors({ doseTimes: 'invalid-dose-times' });
        return;
      }
    }

    if (needsConfirmation(guardResult) && !guardConfirmed) {
      setGuardConfirmError(true);
      document.getElementById('dose-guard-confirm')?.focus();
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    // Fields hidden for the chosen frequency are sent as explicit null; empty
    // notes are null too (never "" or a null boolean the API would reject).
    const notes = parsed.data.notes.trim();
    const payload = {
      product_name: parsed.data.productName.trim(),
      dose_amount: parseDecimal(parsed.data.doseAmount),
      dose_unit: parsed.data.doseUnit.trim(),
      frequency: parsed.data.frequency,
      weekdays: showWeekdays ? [...parsed.data.weekdays].sort((a, b) => a - b) : null,
      interval_days: showIntervalDays ? parseDecimal(parsed.data.intervalDays) : null,
      times_per_day: showTimesPerDay ? parseDecimal(parsed.data.timesPerDay) : null,
      time_of_day: showTimeOfDay ? parsed.data.timeOfDay : showPumpTime ? pumpTimeValue : null,
      dose_times: showPumpDoseTimes ? [...shownDoseTimes].sort() : null,
      dosing_pump: parsed.data.dosingPump,
      start_date: parsed.data.startDate.length > 0 ? parsed.data.startDate : null,
      end_date: parsed.data.endDate.length > 0 ? parsed.data.endDate : null,
      notes: notes.length > 0 ? notes : null,
      dose_guard: toDoseGuardSettings(doseGuard),
    };

    try {
      if (isEdit && schedule) {
        const updateInput: UpdateDosingScheduleInput = payload;
        const updated = await updateDosingSchedule(aquariumId, schedule.id, updateInput);
        const doseChanged = schedule.dose_amount !== payload.dose_amount || JSON.stringify(schedule.dose_guard ?? null) !== JSON.stringify(payload.dose_guard);
        if (doseChanged) await scheduleRetest(payload.dose_guard);
        onSuccess(updated);
      } else {
        const createInput: CreateDosingScheduleInput = payload;
        const created = await createDosingSchedule(aquariumId, createInput);
        await scheduleRetest(payload.dose_guard);
        onSuccess(created);
      }
    } catch (err: unknown) {
      const mapped = toDosingSaveError(err);
      setSaveError(mapped);
      if (mapped.field) {
        setFieldErrors({ [mapped.field]: 'server' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="dosing-form" onSubmit={handleSubmit} noValidate>
      <Stack gap="4">
      {schedule && suggestedDoseAmount !== undefined && (
        <Alert tone="info" live="off" data-testid="dosing-form-voice-suggestion">
          {t('dosing.form.voiceSuggestion', {
            from: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(schedule.dose_amount),
            to: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(suggestedDoseAmount),
            unit: schedule.dose_unit ?? '',
          })}
        </Alert>
      )}
      {saveError !== null && (
        <Alert tone="danger" title={t('dosing.form.saveErrorTitle')} data-testid="dosing-form-save-error">
          {t(saveError.messageKey, saveError.params)}
        </Alert>
      )}

        <div className="dosing-form__field">
          <Input
            id="dosing-product-name"
            name="productName"
            label={t('dosing.form.productName')}
            value={values.productName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('productName', e.target.value)}
            error={fieldErrors.productName ? t('dosing.form.errors.productName') : undefined}
          />
        </div>

        <div className="dosing-form__field">
          <Input
            id="dosing-dose-amount"
            name="doseAmount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            label={t('dosing.form.doseAmount')}
            value={values.doseAmount}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('doseAmount', e.target.value)}
            error={fieldErrors.doseAmount ? t('dosing.form.errors.doseAmount') : undefined}
          />
        </div>

        <div className="dosing-form__field">
          <Input
            id="dosing-dose-unit"
            name="doseUnit"
            label={t('dosing.form.doseUnit')}
            value={values.doseUnit}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('doseUnit', e.target.value)}
            error={fieldErrors.doseUnit ? t('dosing.form.errors.doseUnit') : undefined}
          />
        </div>

        <div className="dosing-form__field">
          <Select
            id="dosing-frequency"
            name="frequency"
            label={t('dosing.form.frequency')}
            className="dosing-form__select"
            value={values.frequency}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('frequency', e.target.value as DosingScheduleFrequency)}
          >
            {FREQUENCY_VALUES.map((freq) => (
              <option key={freq} value={freq}>
                {t(`dosing.form.frequencyOptions.${freq}`)}
              </option>
            ))}
          </Select>
        </div>

        {showWeekdays && (
          <div className="dosing-form__field">
            <Text as="span" id="dosing-weekdays-label" size="sm" weight="medium" className="dosing-form__group-label">
              {t('dosing.form.weekdays')}
            </Text>
            <div
              id="dosing-weekdays"
              role="group"
              aria-labelledby="dosing-weekdays-label"
              aria-describedby={fieldErrors.weekdays ? 'dosing-weekdays-error' : undefined}
              className="dosing-form__weekdays"
            >
              {WEEKDAY_NUMBERS.map((day) => {
                const inputId = `dosing-weekday-${day}`;
                const labelKey = WEEKDAY_LABEL_KEYS[day];
                return (
                  <Input
                    key={day}
                    id={inputId}
                    type="checkbox"
                    label={t(`dosing.form.weekdayLabels.${labelKey}`)}
                    className="dosing-form__weekday-option"
                    inputClassName="dosing-form__checkbox"
                    checked={values.weekdays.includes(day)}
                    onChange={() => toggleWeekday(day)}
                  />
                );
              })}
            </div>
            {fieldErrors.weekdays && (
              <FieldError id="dosing-weekdays-error">{t('dosing.form.errors.weekdays')}</FieldError>
            )}
          </div>
        )}

        {showIntervalDays && (
          <div className="dosing-form__field">
            <Input
              id="dosing-interval-days"
              name="intervalDays"
              type="number"
              inputMode="numeric"
              min={1}
              max={365}
              step={1}
              label={t('dosing.form.intervalDays')}
              value={values.intervalDays}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('intervalDays', e.target.value)}
              error={fieldErrors.intervalDays ? t('dosing.form.errors.intervalDays') : undefined}
            />
          </div>
        )}

        {showTimesPerDay && (
          <div className="dosing-form__field">
            <Input
              id="dosing-times-per-day"
              name="timesPerDay"
              type="number"
              inputMode="numeric"
              min={1}
              max={24}
              step={1}
              label={t('dosing.form.timesPerDay')}
              value={values.timesPerDay}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('timesPerDay', e.target.value)}
              error={fieldErrors.timesPerDay ? t('dosing.form.errors.timesPerDay') : undefined}
            />
          </div>
        )}

        {showTimeOfDay && (
          <div className="dosing-form__field">
            <Input
              id="dosing-time-of-day"
              name="timeOfDay"
              type="time"
              label={t('dosing.form.timeOfDay')}
              value={values.timeOfDay}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('timeOfDay', e.target.value)}
              error={fieldErrors.timeOfDay ? t('dosing.form.errors.timeOfDay') : undefined}
            />
          </div>
        )}

        <div className="dosing-form__field">
          <Checkbox
            id="dosing-pump"
            name="dosingPump"
            label={t('dosing.form.dosingPump')}
            checked={values.dosingPump}
            aria-describedby="dosing-pump-help"
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('dosingPump', e.target.checked)}
          />
          <Text id="dosing-pump-help" variant="muted" size="sm" className="dosing-form__help">
            {t('dosing.form.dosingPumpHelp')}
          </Text>
        </div>

        {showPumpDoseTimes && (
          <div className="dosing-form__field">
            <Text as="span" id="dosing-dose-times-label" size="sm" weight="medium" className="dosing-form__group-label">
              {t('dosing.form.doseTimes')}
            </Text>
            <div
              id="dosing-dose-times"
              role="group"
              aria-labelledby="dosing-dose-times-label"
              aria-describedby={fieldErrors.doseTimes ? 'dosing-dose-times-help dosing-dose-times-error' : 'dosing-dose-times-help'}
              className="dosing-form__dose-times"
            >
              {shownDoseTimes.map((time, index) => (
                <Input
                  key={`dose-slot-${index}`}
                  id={`dosing-dose-time-${index}`}
                  type="time"
                  label={t('dosing.form.doseTimeLabel', { n: index + 1 })}
                  value={time}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleDoseTimeChange(index, e.target.value)}
                />
              ))}
            </div>
            <Text id="dosing-dose-times-help" variant="muted" size="sm" className="dosing-form__help">
              {values.dosingPump ? t('dosing.form.doseTimesHelp') : t('dosing.form.doseTimesHelpManual')}
            </Text>
            {fieldErrors.doseTimes && (
              <FieldError id="dosing-dose-times-error">{t('dosing.form.errors.doseTimes')}</FieldError>
            )}
          </div>
        )}

        {showPumpTime && (
          <div className="dosing-form__field">
            <Input
              id="dosing-pump-time"
              name="pumpTime"
              type="time"
              label={t('dosing.form.pumpTime')}
              value={pumpTimeValue}
              helperText={values.dosingPump ? undefined : t('dosing.form.doseTimesHelpManual')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('timeOfDay', e.target.value)}
            />
          </div>
        )}

        <DoseGuardFields
          aquariumId={aquariumId}
          values={doseGuard}
          onChange={setDoseGuard}
          doseAmount={Number.isFinite(parseDecimal(values.doseAmount)) && parseDecimal(values.doseAmount) > 0 ? parseDecimal(values.doseAmount) : null}
          doseUnit={values.doseUnit}
          timesPerDay={showTimesPerDay && timesPerDayCount !== null ? timesPerDayCount : null}
          confirmed={guardConfirmed}
          onConfirmedChange={(value) => {
            setGuardConfirmed(value);
            if (value) setGuardConfirmError(false);
          }}
          onResult={setGuardResult}
          confirmError={guardConfirmError}
        />

        <div className="dosing-form__dates">
          <Input
            id="dosing-start-date"
            name="startDate"
            type="date"
            label={t('dosing.form.startDate')}
            value={values.startDate}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('startDate', e.target.value)}
          />
          <Input
            id="dosing-end-date"
            name="endDate"
            type="date"
            label={t('dosing.form.endDate')}
            value={values.endDate}
            min={values.startDate || undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('endDate', e.target.value)}
            error={fieldErrors.endDate ? t('dosing.form.errors.endDate') : undefined}
          />
        </div>

        <div className="dosing-form__field">
          <Textarea
            id="dosing-notes"
            name="notes"
            label={t('dosing.form.notes')}
            value={values.notes}
            maxLength={1000}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('notes', e.target.value)}
            error={fieldErrors.notes ? t('dosing.form.errors.notes') : undefined}
          />
        </div>

        <Inline gap="3" justify="end" className="dosing-form__actions">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('dosing.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {t('dosing.form.submit')}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
